import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'

import { useActiveBrand, useActiveLocation } from '@/core/store'
import { OlympusClient } from '@/OlympusClient'
import { useReactiveFeatureFlag } from '@/utils/useFeatureFlags'

import { PRO_UPSELL_PERSONALIZED_PREVIEW_FLAG } from '../featureFlags'

export const DEFAULT_PRO_UPSELL_STATUS_LABEL = 'Open until 10:00 PM'

export interface PersonalizedProUpsellPreviewData {
  address: string
  appIconBackgroundColor: string
  appIconUrl: string | null
  brandName: string
  heroImageUrl: string
  locationName: string
  statusLabel: string
}

export interface PersonalizedProUpsellPreviewInputs {
  address?: string | null
  appIconBackgroundColor?: string | null
  appIconUrl?: string | null
  brandName?: string | null
  heroImageUrl?: string | null
  locationName?: string | null
  statusLabel?: string | null
}

type LandingHeroSections = Awaited<
  ReturnType<typeof OlympusClient.funnel.v1.sections.getSections>
>

interface StoreDay {
  hours: Array<{ end: string }>
  open: boolean
}

/**
 * Normalizes nullable API string fields into a usable display value.
 *
 * @param value - Nullable source field.
 */
function nonEmptyString(value: string | null | undefined): string | null {
  const trimmedValue = value?.trim()
  return trimmedValue || null
}

/**
 * Selects the visual asset used by the current website landing hero.
 *
 * @param sections - Landing-hero sections returned by Olympus.
 */
export function getLandingHeroImageUrl(
  sections: LandingHeroSections | undefined
): string | null {
  const landingHero = sections?.results.find(
    (section) => section.type === 'landing-hero'
  )

  if (!landingHero) return null

  if (landingHero.params.assetType === 'video') {
    return nonEmptyString(landingHero.params.video?.thumbnail)
  }

  return nonEmptyString(landingHero.params.carouselAssets[0]?.image)
}

/**
 * Formats the selected day's store hours for the storefront preview.
 *
 * @param storeHours - Store-hours schedule keyed by day.
 * @param dayIndex - JavaScript day index, supplied in tests for deterministic output.
 */
export function getStatusLabel(
  storeHours: Record<string, StoreDay> | undefined,
  dayIndex = new Date().getDay()
): string {
  const dayNames = [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ]
  const today = storeHours?.[dayNames[dayIndex] ?? 'sunday']

  if (today?.open === true && today.hours.length === 0) {
    return 'Open 24 hours'
  }

  const latestEndTime = today?.open
    ? today.hours[today.hours.length - 1]?.end
    : undefined

  return latestEndTime
    ? `Open until ${latestEndTime}`
    : DEFAULT_PRO_UPSELL_STATUS_LABEL
}

/**
 * Returns null unless every storefront field needed for a coherent preview exists.
 *
 * @param inputs - Optional branding and location fields collected by the composable.
 */
export function buildPersonalizedProUpsellPreviewData(
  inputs: PersonalizedProUpsellPreviewInputs
): PersonalizedProUpsellPreviewData | null {
  const {
    address,
    appIconBackgroundColor,
    appIconUrl,
    brandName,
    heroImageUrl,
    locationName,
    statusLabel,
  } = inputs
  const completePreviewData = {
    address: nonEmptyString(address),
    brandName: nonEmptyString(brandName),
    heroImageUrl: nonEmptyString(heroImageUrl),
    locationName: nonEmptyString(locationName),
  }

  if (
    !completePreviewData.address ||
    !completePreviewData.brandName ||
    !completePreviewData.heroImageUrl ||
    !completePreviewData.locationName
  ) {
    return null
  }

  return {
    address: completePreviewData.address,
    appIconBackgroundColor: nonEmptyString(appIconBackgroundColor) ?? 'white',
    appIconUrl: nonEmptyString(appIconUrl),
    brandName: completePreviewData.brandName,
    heroImageUrl: completePreviewData.heroImageUrl,
    locationName: completePreviewData.locationName,
    statusLabel: nonEmptyString(statusLabel) ?? DEFAULT_PRO_UPSELL_STATUS_LABEL,
  }
}

/** Loads and normalizes the active brand's optional Pro upsell storefront preview. */
export function usePersonalizedProUpsellPreview(): {
  appIconBackgroundColor: ComputedRef<string>
  appIconUrl: ComputedRef<string | null>
  isReady: ComputedRef<boolean>
  previewData: ComputedRef<PersonalizedProUpsellPreviewData | null>
} {
  const { activeBrandId, locations, logo, name } = useActiveBrand()
  const { activeLocation } = useActiveLocation()
  const { enabled: personalizedPreviewEnabled } = useReactiveFeatureFlag(
    PRO_UPSELL_PERSONALIZED_PREVIEW_FLAG
  )
  const queryEnabled = computed(
    () => personalizedPreviewEnabled.value && Boolean(activeBrandId.value)
  )

  const brandingQuery = useQuery({
    enabled: queryEnabled,
    queryFn: async () =>
      await OlympusClient.website.v1.brands.modules.branding.get({
        params: { brandId: activeBrandId.value },
        query: {},
      }),
    queryKey: computed(() => [
      'upsell',
      'personalized-preview',
      'branding',
      activeBrandId.value,
    ]),
  })
  const landingHeroQuery = useQuery({
    enabled: queryEnabled,
    queryFn: async () =>
      await OlympusClient.funnel.v1.sections.getSections({
        query: { brandId: activeBrandId.value, type: 'landing-hero' },
      }),
    queryKey: computed(() => [
      'upsell',
      'personalized-preview',
      'landing-hero',
      activeBrandId.value,
    ]),
  })
  const mobileAppQuery = useQuery({
    enabled: queryEnabled,
    queryFn: async () =>
      await OlympusClient.brandsV0.brand.getMobileApp({
        params: { brandId: activeBrandId.value },
      }),
    queryKey: computed(() => [
      'upsell',
      'personalized-preview',
      'mobile-app',
      activeBrandId.value,
    ]),
  })

  const isReady = computed(
    () =>
      !personalizedPreviewEnabled.value ||
      !activeBrandId.value ||
      (!brandingQuery.isPending.value &&
        !landingHeroQuery.isPending.value &&
        !mobileAppQuery.isPending.value)
  )
  const appIconUrl = computed(() => {
    if (!personalizedPreviewEnabled.value || !activeBrandId.value) return null

    return (
      nonEmptyString(mobileAppQuery.data.value?.theme.icon.url) ??
      nonEmptyString(brandingQuery.data.value?.state.brandLogoUrl) ??
      nonEmptyString(logo.value)
    )
  })
  const appIconBackgroundColor = computed(() => {
    if (!personalizedPreviewEnabled.value || !activeBrandId.value) {
      return 'white'
    }

    return (
      nonEmptyString(mobileAppQuery.data.value?.theme.icon.backgroundColor) ??
      'white'
    )
  })
  const previewLocation = computed(
    () => activeLocation.value ?? locations.value[0]
  )
  const previewData = computed(() => {
    if (
      !personalizedPreviewEnabled.value ||
      !activeBrandId.value ||
      !isReady.value ||
      brandingQuery.isError.value ||
      landingHeroQuery.isError.value ||
      mobileAppQuery.isError.value
    ) {
      return null
    }

    return buildPersonalizedProUpsellPreviewData({
      address: previewLocation.value?.address?.full,
      appIconBackgroundColor: appIconBackgroundColor.value,
      appIconUrl: appIconUrl.value,
      brandName: name.value,
      heroImageUrl: getLandingHeroImageUrl(landingHeroQuery.data.value),
      locationName: previewLocation.value?.name,
      statusLabel: getStatusLabel(
        previewLocation.value?.hours.store as
          | Record<string, StoreDay>
          | undefined
      ),
    })
  })

  return { appIconBackgroundColor, appIconUrl, isReady, previewData }
}
